import { Getter } from 'vuex-simple';
import i18next, { i18n, ResourceKey, TFunction } from 'i18next';
import { findAttrByName } from '@/Common/Utils/General/Modules/translate';
import Vue, { getCurrentInstance, ComponentOptions, computed } from 'vue';
import BaseBundlerModule from '@/HostApp/services/CSSBundler/BaseBundlerModule';

const DEFAULT_LNG = 'en_US';
const FALLBACK_NS = 'common';
const NS_SEPARATOR = ':::';
const PACKAGE_PREFIX = 'package_';

class TranslationService implements App.TranslationService {
	ns!: string;

	constructor(ns: string) {
		this.ns = ns;
	}

	public setLanguage = i18nServiceModule.setLanguage.bind(i18nServiceModule);
	public currentLanguage = computed(() => i18nServiceModule.currentLanguage);

	public async setNamespace(ns: string): Promise<ResourceKey | void> {
		if (ns) {
			const [res] = await i18nServiceModule.loadPackageTranslations([ns]);
			this.ns = ns.startsWith(PACKAGE_PREFIX) ? ns : ns === FALLBACK_NS ? FALLBACK_NS : `${PACKAGE_PREFIX}${ns}`;
			return res.store.data[res.language][this.ns];
		}
	}
}

export const getTranslationFunction = (lng?: string, ns?: string): TFunction<string, Common.IMap<string>, string> => {
	const genericT = i18next.t.bind(i18next);

	if (lng) {
		return i18next.getFixedT(lng, ns);
	} else if (ns) {
		return i18next.getFixedT(null, ns);
	} else {
		return genericT;
	}
};

const nsVueMixin: ComponentOptions<Vue> = {
	beforeCreate(): void {
		const { customElement, translationNamespace: tNs } = this.$options as TRANSLATE.ICustomOptions;

		if (this.$options.name === 'shadow-root' && customElement) {
			const packageId = tNs || findAttrByName(customElement, 'packageId');
			this.$root.$i18n_service = new TranslationService(
				packageId ? `${PACKAGE_PREFIX}${packageId}` : FALLBACK_NS
			);
		}

		const ns = tNs || this.$parent?.$i18n_service?.ns || this.$root?.$i18n_service?.ns;
		this.$i18n_service = new TranslationService(ns || FALLBACK_NS);
	}
};

export class I18nServiceModule extends BaseBundlerModule {
	private readonly storage: Map<string, Promise<Record<string, string>>> = new Map();

	constructor(language = DEFAULT_LNG, mode: RequestMode = 'cors') {
		super(mode);
		this.prepareVue();
	}

	@Getter()
	public get currentLanguage(): string {
		return i18next.language;
	}

	public setLanguage(lng: string) {
		this.onBeforeLanguageChange();
		i18next.language = this.normalizeLanguage(lng);
	}

	private normalizeLanguage(language: string): string {
		return language.replace('-', '_');
	}

	private onBeforeLanguageChange(): void {
		// implement in case runtime local switching be required
	}

	public async init(language: string = DEFAULT_LNG): Promise<TFunction> {
		const lng = this.normalizeLanguage(language);
		return i18next.init({
			lng,
			fallbackNS: FALLBACK_NS,
			nsSeparator: NS_SEPARATOR,
			keySeparator: '::',
			pluralSeparator: '@@',
			contextSeparator: '@@',
			interpolation: {
				unescapePrefix: '--',
				prefix: '__',
				suffix: '__',
				escapeValue: false
			}
		});
	}

	public loadPackageTranslations(packageIds: string | string[]): Promise<Array<i18n>> {
		packageIds = [packageIds].flat();

		const loaders: Array<Promise<i18n>> = [];

		for (const packageId of packageIds) {
			const ns = `${PACKAGE_PREFIX}${packageId}`;
			const url = `/aps/2/packages/${packageId}/i18n/${i18next.language}.json`;
			loaders.push(this.loadResourceFromUrl(url, ns));
		}

		return Promise.all(loaders);
	}

	public async loadDictionary(): Promise<void | i18n> {
		const url = `/aps/2/ui/runtime/client/aps/i18n/dictionary/${i18next.language}.json`;
		return this.loadResourceFromUrl(url, FALLBACK_NS);
	}

	private async loadResourceFromUrl(url: string, ns: string): Promise<i18n> {
		this.fetchUrls([url], this.storage, this.createI18nNamespaceNode);
		return this.addResourceBundleToPlugin(url, ns);
	}

	protected createI18nNamespaceNode(data: Promise<string>): Promise<Record<string, string>> {
		return data.then((values) => {
			let result = {};

			try {
				result = JSON.parse(values);
			} catch (e) {
				// eslint-disable-next-line no-console
				console.warn('UX1->i18nServiceModule: cannot parse translation JSON: ', values);
			}

			return result;
		});
	}

	private addResourceBundleToPlugin(url: string, ns: string): Promise<i18n> {
		return (this.storage.get(url) as Promise<Common.IMap<string>>).then((translations) => {
			return i18next.addResourceBundle(i18next.language, ns, translations);
		});
	}

	private prepareVue(): void {
		Vue.prototype.$t = function (
			key: string,
			options: Common.IMap<string> = {},
			interpolationMap: Common.IMap<string> = {},
			namespace: string
		) {
			const instance = this || getCurrentInstance()?.proxy;

			if (namespace && namespace !== FALLBACK_NS && !namespace.startsWith(PACKAGE_PREFIX)) {
				namespace = `${PACKAGE_PREFIX}${namespace}`;
			}

			const ns = namespace || instance?.$i18n_service?.ns || FALLBACK_NS;
			const t = getTranslationFunction(i18nServiceModule.currentLanguage, ns);

			return t(key, options);
		};

		Vue.mixin(nsVueMixin);
	}
}

const i18nServiceModule = new I18nServiceModule();

export default i18nServiceModule;
